// BFSG
import * as React from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as Circle } from 'assets/shapes/icon_circle.svg'
import { ReactComponent as Oval } from 'assets/shapes/icon_oval.svg'
import { ReactComponent as Rectangle } from 'assets/shapes/icon_rectangle.svg'
import { ReactComponent as Square } from 'assets/shapes/icon_square.svg'
import theme from 'theme'
import CheckRadio from 'theme/atoms/CheckRadio'
import { useCustomTailor } from 'modules/productDetail'
import { useDisplayVariant } from 'modules/productDetail'
import useTranslations from 'hooks/useTranslations'
import Amount from 'theme/atoms/Amount'

export default function CustomTailor() {
  const customTailor = useCustomTailor()
  const t = useTranslations<'templates-PDP'>()
  const variant = useDisplayVariant()
  const sizeUnit = variant.data.attributes.WIDTH_MIN?.values[0].unit || 'cm'
  const configurationData =
    variant.data.configurations.custom_tailor.configuration_data

  // MAX & MIN WIDTH & LENGTH
  const minWidth = variant.data.attributes.WIDTH_MIN?.values[0].value || 0
  const maxWidth = variant.data.attributes.WIDTH_MAX?.values[0].value || 9999
  const minLength = variant.data.attributes.LENGTH_MIN?.values[0].value || 0
  const maxLength = variant.data.attributes.LENGTH_MAX?.values[0].value || 9999

  const radioRef = React.useRef<HTMLLIElement | null>(null)
  const $widthAmount = React.useRef<HTMLDivElement>(null)
  const $lengthAmount = React.useRef<HTMLDivElement>(null)

  const setWidth = (val) => {
    const sizeObject = {
      ...customTailor.data.size,
      width: val
    }

    customTailor.set('size', sizeObject)
  }
  const setLength = (val) => {
    const sizeObject = {
      ...customTailor.data.size,
      length: val
    }

    customTailor.set('size', sizeObject)
  }

  const getTailorShapeIcon = (shape: string) => {
    switch (shape) {
      case 'round':
        return <Circle />
      case 'oval':
        return <Oval />
      case 'rectangular':
        return <Rectangle />
      case 'square':
        return <Square />
      default:
        return null
    }
  }
  const handleBrink = (type, length?) => {
    if (length) {
      return customTailor.set('brink', { type: type, width: length })
    } else {
      return customTailor.set('brink', {
        type: type,
        width: configurationData?.brinks.find((brink) => brink.type === type)
          ?.length[0]
      })
    }
  }

  const handleShape = (shape) => {
    if (shape === 'round' || shape === 'square') {
      customTailor.set('size', { ...customTailor.data.size, length: null })
    }
    if ((shape === 'round' || shape === 'oval') && customTailor.data.brink) {
      customTailor.set('brink', { type: 'normal', width: 1 })
    }
    customTailor.set('shape', shape)
  }

  if (!variant.data.configurations.custom_tailor.is_custom_tailor) return null

  return (
    <Wrapper className="CustomTailor" data-cy-collection="CustomTailor">
      <div className="Filter-shape Filter">
        <h3 className="label">{t('shape_label')}</h3>
        <ul
          className="options shapes"
          data-cy-state={customTailor.data?.shape && 'shape-selected'}
        >
          {configurationData?.shapes.map((shape) => (
            <ShapeOption
              key={shape}
              data-cy-handle="shape-option"
              $selectable={true}
              $selected={customTailor.data?.shape === shape}
              onClick={() => handleShape(shape)}
              tabIndex={0}
              aria-label={t.asText(
                false,
                'filterdrawer_customtailor_shape_filter',
                {
                  option: t.asText(false, shape)
                }
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleShape(shape)
                }
              }}
            >
              <div className="svg-wrapper">{getTailorShapeIcon(shape)}</div>
              <div className="icon-description">{t(shape)}</div>
            </ShapeOption>
          ))}
        </ul>
      </div>
      <div className="Filter-size Filter" data-cy-state="size-input">
        <h3 className="label">{t('desired_size')}</h3>
        <ul className="options" data-cy-state="size-options">
          <div className="amount-wrapper">
            <div>
              <div className="amount">
                <Amount
                  data-cy-handle-amount="width-input"
                  data-cy-handle-decrease="decrease-width"
                  data-cy-handle-increase="increase-width"
                  customTailorInitialNullValue={true}
                  disabled={!customTailor.data.shape}
                  minValue={typeof minWidth === 'string' ? 0 : minWidth}
                  maxValue={typeof maxWidth === 'string' ? 0 : maxWidth}
                  setValue={setWidth}
                  value={customTailor.data.size.width || undefined}
                  ref={$widthAmount}
                  decrease_button_a11y_title={t.asText(
                    $widthAmount as React.RefObject<HTMLElement>,
                    'templates_PDP_filterdrawer_customtailor_a11y_decrease_width_button'
                  )}
                  amount_input_a11y_title={t.asText(
                    $widthAmount,
                    'templates_PDP_filterdrawer_customtailor_a11y_width_amount_input'
                  )}
                  increase_button_a11y_title={t.asText(
                    $widthAmount,
                    'templates_PDP_filterdrawer_customtailor_a11y_increase_width_button'
                  )}
                />
                <div className="amount-description">
                  {/* {t('width_info', { width: minWidth, unit: sizeUnit })} */}
                  <div className="unit">
                    {t('width_unit_info', { unit: sizeUnit })}
                  </div>
                  <div className="minmax">
                    {t('min_max_info', {
                      min_width: minWidth,
                      max_width: maxWidth,
                      unit: sizeUnit
                    })}
                  </div>
                </div>
              </div>
            </div>
            {(customTailor.data?.shape === 'rectangular' ||
              customTailor.data?.shape === 'oval') && (
              <div data-cy-state="length-selectable">
                <div className="amount">
                  <Amount
                    data-cy-handle-amount="length-input"
                    data-cy-handle-decrease="decrease-length"
                    data-cy-handle-increase="increase-length"
                    customTailorInitialNullValue={true}
                    minValue={typeof minLength === 'string' ? 0 : minLength}
                    maxValue={typeof maxLength === 'string' ? 0 : maxLength}
                    setValue={setLength}
                    value={customTailor.data.size.length || undefined}
                    ref={$lengthAmount}
                    decrease_button_a11y_title={t.asText(
                      $widthAmount,
                      'templates_PDP_filterdrawer_customtailor_a11y_decrease_length_button'
                    )}
                    amount_input_a11y_title={t.asText(
                      $widthAmount,
                      'templates_PDP_filterdrawer_customtailor_a11y_length_amount_input'
                    )}
                    increase_button_a11y_title={t.asText(
                      $widthAmount,
                      'templates_PDP_filterdrawer_customtailor_a11y_increase_length_button'
                    )}
                  />
                  <div className="amount-description">
                    {/* {t('length_info', { length: minLength, unit: sizeUnit })} */}
                    <div className="unit">
                      {t('length_unit_info', { unit: sizeUnit })}
                    </div>
                    <div className="minmax">
                      {t('min_max_info', {
                        min_width: minLength,
                        max_width: maxLength,
                        unit: sizeUnit
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ul>
      </div>
      <div className="Filter-brink Filter">
        <h3 className="label">{t('brink_label')}</h3>
        <ul
          className="options brinks"
          data-cy-state={customTailor.data?.brink && 'brink-selected'}
        >
          {configurationData?.brinks.map((brink) => (
            <BrinkOption
              key={brink.type}
              className="hem-option"
              $selected={customTailor.data?.brink?.type === brink.type}
              onClick={() =>
                brink.activeAt.includes(customTailor.data.shape || '') &&
                handleBrink(brink.type)
              }
              data-cy-handle="set-brink"
              $selectable={brink.activeAt.includes(
                customTailor.data.shape || ''
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  brink.activeAt.includes(customTailor.data.shape || '') &&
                    handleBrink(brink.type)
                }
              }}
              aria-label={t.asText(
                false,
                'filterdrawer_customtailor_brink_filter',
                {
                  option: t.asText(false, `${brink.type}_brink_type`)
                }
              )}
              tabIndex={0}
            >
              <img
                src={
                  brink.type === 'normal'
                    ? 'https://res.cloudinary.com/lusini/image/upload/v1684324618/application/products/normalsaum.jpg'
                    : 'https://res.cloudinary.com/lusini/image/upload/v1684324622/application/products/kuvertsaum.jpg'
                }
                alt={brink.type}
              />
              <div className="icon-description">
                {t.asText(radioRef, `${brink.type}_brink_type`)}
              </div>
              {brink.type === 'mitred' && (
                <div data-cy-state="brink-has-surcharge" className="surcharge">
                  {t('surcharge_brink')}
                </div>
              )}
            </BrinkOption>
          ))}
        </ul>
      </div>
      {customTailor.data.brink && (
        <div
          className="Filter-brink-width Filter"
          data-cy-state="brink-width-selected"
        >
          <h3 className="label">{t('brink_width_label')}</h3>
          <ul className="brink-width options">
            {configurationData?.brinks
              .find((brink) => brink.type === customTailor.data.brink?.type)
              ?.length.map((width) => (
                <li key={width}>
                  <CheckRadio
                    checked={width === customTailor.data.brink?.width}
                    data-cy-handle="set-brink-width"
                    radiogroup="brink-width"
                    onClick={() =>
                      handleBrink(customTailor.data.brink?.type, width)
                    }
                    variation="radio"
                    label={width + sizeUnit}
                    ariaLabel={t.asText(
                      false,
                      'filterdrawer_customtailor_brinkwidth_filter',
                      {
                        option: width + sizeUnit
                      }
                    )}
                  />
                </li>
              ))}
          </ul>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  > .Filter {
    display: block;
    align-items: center;
    border-bottom: 1px solid ${theme.colors.shade.b2};
    padding: ${theme.spacing('ml')} 0;
    margin: 0 ${theme.spacing('ml')};
    &:first-child {
      padding-top: 0;
    }

    > h3 {
      min-width: 5rem;
      ${theme.ty('r-base', null, 'r-xl')}
      color: ${theme.colors.b0};
    }

    > .value {
      flex: 1;
      color: ${theme.colors.shade.b3};
      margin-left: ${theme.spacing('s')};
    }

    > .options {
      display: flex;
      position: relative;
      flex-wrap: wrap;
      height: 100%;
      margin-top: ${theme.spacing('s')};

      &.shapes {
        @media (min-width: ${theme.ms.MD}px) {
          justify-content: space-between;
        }
      }

      &.brinks {
        display: flex;
        flex-wrap: nowrap;
        > .hem-option {
          margin-bottom: ${theme.spacing('s')};
        }
      }
      &.brink-width {
        display: flex;
        > li {
          margin-right: ${theme.spacing('s')};
        }
      }

      > .amount-wrapper {
        > div {
          > .amount {
            display: flex;
            margin-right: ${theme.spacing('m')};
            margin-bottom: ${theme.spacing('xs')};
            height: 2.625rem;
            > label {
              height: 2.625rem;
              > button {
                width: 2.625rem;
                height: 100%;
              }
              > input {
                width: 3.25rem;
                height: 100%;
              }
            }
            > .amount-description {
              ${theme.ty('r-base')};
              color: ${theme.colors.b0};
              > .minmax {
                ${theme.ty('r-s')};
                color: ${theme.colors.shade.b2};
              }

              margin-left: ${theme.spacing('xs')};
            }
          }
        }
      }

      > .hem-option {
        margin-right: ${theme.spacing('xs')};
      }
    }
  }
`
const BrinkOption = styled.li<{ $selected: boolean; $selectable: boolean }>`
  flex-grow: 1;
  border: 1px solid ${theme.colors.shade.b3};
  padding: ${theme.spacing('s')};
  ${theme.ty('rc-base')};
  ${(p) =>
    !p.$selectable &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}

  ${(p) =>
    p.$selected &&
    css`
      border: 1px solid ${theme.colors.b0};
    `}
  
  
  > .icon-description {
    margin-top: ${theme.spacing('xs')};
  }
  > .surcharge {
    ${theme.ty('r-s')};
  }

  > img {
    width: 100%;
  }
`
const ShapeOption = styled.li<{ $selected: boolean; $selectable: boolean }>`
  ${theme.rounding('s')}
  justify-content: center;
  position: relative;
  align-items: center;
  width: 15%;
  overflow: hidden;
  margin-right: ${theme.spacing('m')};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  flex-basis: 50%;
  justify-content: unset;
  margin: 0;

  @media (min-width: ${theme.ms.MD}px) {
    justify-content: unset;
    display: block;
    flex-direction: unset;
    flex-wrap: unset;
    flex-basis: unset;
    justify-content: center;
    margin-right: ${theme.spacing('m')};
  }
  > .icon-description {
    text-align: center;
    margin-left: ${theme.spacing('s')};
    ${theme.ty('r-base', null, 'r-s')}
    @media (min-width: ${theme.ms.MD}px) {
      margin: 0;
      align-self: center;
      ${theme.ty('r-s')}
    }
  }
  > .svg-wrapper {
    width: 50px;
    align-self: flex-start;
    @media (min-width: ${theme.ms.MD}px) {
      align-self: center;
      width: 100%;
    }
    height: 50px;
    display: flex;
    align-items: center;
    > svg {
      height: 32px;
      width: 100%;

      ${(p) =>
        p.$selected &&
        css`
          > g {
            > rect {
              stroke: ${theme.colors.b0};
            }
            > circle {
              stroke: ${theme.colors.b0};
            }
          }
        `}
    }
    > img {
      width: 100%;
      height: 100%;
    }
  }
  ${(p) =>
    !p.$selectable &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
      &:after {
        content: '';
        position: absolute;
        width: 200%;
        left: -50%;
        top: calc(50% - 1px);
        height: 1px;
        background: ${theme.colors.shade.b3};
        transform: rotate(-45deg);
      }
    `}
  ${(p) =>
    p.$selected &&
    css`
      &:after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${theme.colors.b0};
        position: absolute;
        left: 15%;
        top: 50%;
        transform: translate(-50%, -50%);
        @media (min-width: ${theme.ms.MD}px) {
          left: 50%;
          top: 37%;
        }
      }
    `}
`
